// src/components/LegalModal.tsx
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface LegalModalProps {
  open: boolean;
  onClose: () => void;
  type: 'terms' | 'privacy';
}

const termsOfService = `
Terms of Service

Last updated: 13-June-2025

1. ACCEPTANCE OF TERMS
By accessing and using Field Sense AI, you accept and agree to be bound by the terms and provision of this agreement.

2. DESCRIPTION OF SERVICE
Field Sense AI provides ecological audio data analysis services using artificial intelligence models to identify bird species and other wildlife sounds.

3. USER ACCOUNTS
- You are responsible for maintaining the confidentiality of your account
- You agree to provide accurate and complete information
- You are responsible for all activities under your account

4. USE OF SERVICE
- You may use our service for lawful purposes only
- You agree not to misuse our service or help anyone else do so
- You are responsible for your data and content

5. DATA AND PRIVACY
- We collect and process data as described in our Privacy Policy
- You retain ownership of your uploaded audio files
- We may use aggregated, anonymized data to improve our services

6. INTELLECTUAL PROPERTY
- Our service and its original content remain our property
- You grant us license to use your data for service provision
- You may not copy, modify, or distribute our content without permission

7. LIMITATION OF LIABILITY
Our service is provided "as is" without warranties. We are not liable for indirect, incidental, or consequential damages.

8. CHANGES TO TERMS
We reserve the right to modify these terms at any time. Continued use constitutes acceptance of changes.

9. CONTACT INFORMATION
For questions about these terms, contact us at support@fieldsense.ai
`;

const privacyPolicy = `
Privacy Policy

Last updated: 13-June-2025

1. INFORMATION WE COLLECT
- Account information (name, email, profile data)
- Audio files you upload for analysis
- Usage data and analytics
- Device and browser information

2. HOW WE USE INFORMATION
- To provide and improve our services
- To communicate with you about your account
- To analyze usage patterns and improve our AI models
- To ensure security and prevent fraud

3. INFORMATION SHARING
- We do not sell your personal information
- We may share data with service providers who help operate our platform
- We may disclose information if required by law
- Aggregated, anonymized data may be used for research

4. DATA STORAGE AND SECURITY
- Your data is stored securely using industry-standard encryption
- Audio files are processed and may be retained for service improvement
- You can request deletion of your data at any time

5. YOUR RIGHTS
- Access and update your personal information
- Request deletion of your data
- Opt out of certain communications
- Export your data in portable formats

6. COOKIES AND TRACKING
- We use cookies to improve user experience
- You can control cookie settings in your browser
- We may use analytics tools to understand usage patterns

7. CHILDREN'S PRIVACY
Our service is not intended for children under 13. We do not knowingly collect data from children.

8. INTERNATIONAL USERS
Your data may be processed in countries other than your own, subject to appropriate safeguards.

9. CHANGES TO POLICY
We will notify you of significant changes to this privacy policy via email or service notifications.

10. CONTACT US
For privacy questions or requests, contact us at support@fieldsense.ai
`;

const LegalModal: React.FC<LegalModalProps> = ({ open, onClose, type }) => {
  const title = type === 'terms' ? 'Terms of Service' : 'Privacy Policy';
  const content = type === 'terms' ? termsOfService : privacyPolicy;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: '80vh',
          bgcolor: 'background.paper',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h5" component="div" fontWeight="bold">
          {title}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
          <Typography variant="body2" component="div">
            {content}
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={onClose} 
          variant="contained"
          sx={{ 
            bgcolor: '#00C853',
            color: 'white',
            '&:hover': { bgcolor: '#00A847' }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LegalModal;

